// src/services/vocabularyService.js
//
// PRD-002 estabilização (2026-05-12) — wrapper do endpoint /vocabulary
// do regula-hub. Decisão arquitetural: tradução de domínio (labels,
// descriptions, variants, icons) vive no backend. Este service consome,
// suporta conditional GET (ETag/If-None-Match) e retorna o payload cru.
//
// A camada de cache fica no Pinia store (stores/vocabulary.js).
// Uses requestWithAuth (standard API pattern) — hub endpoints routed via
// /api/regulahub/v1/... through Kong (prod) or dev proxy.

import { logger } from "@/utils/logger";
import { HUB_BASE, hubHeaders, requestWithAuth } from "./regulaHubBase";

const ENDPOINT = `${HUB_BASE}/admin/vocabulary`;

/**
 * Busca o vocabulário do regula-hub.
 *
 * @param {object} opts
 * @param {string} [opts.lang='pt-BR'] - tag IETF
 * @param {string} [opts.etag] - ETag salvo de fetch anterior; se bater no
 *                                backend, retorna 304 e este service devolve
 *                                `{ notModified: true }` sem payload.
 * @returns {Promise<{notModified: false, data: object, etag: string}
 *                 | {notModified: true}>}
 */
export async function fetchVocabulary({ lang = "pt-BR", etag = null } = {}) {
  const extraHeaders = { ...hubHeaders() };
  if (etag) {
    extraHeaders["If-None-Match"] = etag;
  }

  let response;
  try {
    response = await requestWithAuth(
      `${ENDPOINT}?lang=${encodeURIComponent(lang)}`,
      {},
      extraHeaders,
    );
  } catch (err) {
    logger.error("vocabulary fetch network error:", err);
    throw err;
  }

  if (response.status === 304) {
    return { notModified: true };
  }

  if (!response.ok) {
    throw new Error(`vocabulary endpoint retornou ${response.status}`);
  }

  const data = await response.json();
  const newEtag = response.headers.get("etag") || response.headers.get("ETag");
  return { notModified: false, data, etag: newEtag };
}
